"use client";

import React, { useState } from "react";
import { MessageSquare, ShieldCheck, MapPin, Zap, Layers, X, ExternalLink, Share2, Check, ShoppingCart } from "lucide-react";
import { getCurrencyByCode } from "@/lib/countries";
import VehicleBrandLogo from "@/components/VehicleBrandLogo";
import CategoryIcon from "@/components/CategoryIcon";
import { generateProductSlug } from "@/lib/catalog-helpers";
import { useCart } from "@/context/CartContext";

export interface ProductCardProps {
  product: {
    id: string;
    slug?: string;
    canonicalUrl?: string;
    sku?: string;
    title?: string;
    name?: string;
    price?: number;
    priceInCents?: number;
    currency?: string;
    category?: string;
    stockCount?: number;
    stock?: number;
    stockQuantity?: number;
    imageUrl?: string;
    imageUrls?: string[];
    description?: string;
    dispatchTime?: string;
    condition?: string;
    make?: string;
    model?: string;
    year?: string | number;
    vendor?: {
      name: string;
      area?: string;
      fullAddress?: string;
      isVerified?: boolean;
    };
    seller?: {
      id?: string;
      storeName?: string;
      name?: string;
      shopLocation?: string;
      location?: string;
      address?: string;
      phone?: string;
      isVerified?: boolean;
    };
    whatsappUrl?: string;
  };
  activeVehicle?: {
    make: string;
    model: string;
    year: number | string;
    trim?: string;
    engine?: string;
  } | null;
  apiBaseUrl?: string;
  priority?: boolean;
  onOrderInitiated?: (orderId: string) => void;
  onProductClick?: (product: any) => void;
  onSelect?: (product: any) => void;
  onVendorClick?: (vendor: any) => void;
  whatsappUrl?: string;
  country?: string;
}

export const ProductCard: React.FC<ProductCardProps> = ({
  product,
  activeVehicle,
  onProductClick,
  onSelect,
  onVendorClick,
  country = "NG",
}) => {
  const [isSpecsOpen, setIsSpecsOpen] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const { addToCart } = useCart();

  const title = product.title || product.name || "Automotive Component";
  const sku = product.sku || "P4D-0000-GEN";
  const rawPrice = product.price ?? (product.priceInCents != null ? product.priceInCents / 100 : 0);
  const targetCountry = (country || (product as any).country_code || (product as any).country || 'NG').toUpperCase();
  const rawCurrency = (product.currency || (targetCountry === 'GH' ? 'GHS' : targetCountry === 'KE' ? 'KES' : targetCountry === 'ZA' ? 'ZAR' : 'NGN')).toUpperCase();
  const currObj = getCurrencyByCode(rawCurrency);
  const currencySymbol = currObj.symbol || (currObj.code === 'GHS' ? 'GH₵' : currObj.code === 'XOF' ? 'CFA' : '₦');
  const currency = currencySymbol;
  const stockCount = product.stockCount ?? product.stock ?? product.stockQuantity ?? 1;

  const productSlug =
    product.slug ||
    generateProductSlug({
      make: product.make,
      model: product.model,
      year: product.year,
      title: product.title || product.name,
      id: product.id,
      sku: product.sku
    });

  const canonicalUrl =
    product.canonicalUrl ||
    `https://parts4drive.com/${country.toLowerCase()}/products/${productSlug}`;

  const handleCopyLink = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(canonicalUrl);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    }
  };
  
  const imageUrl =
    product.imageUrl ||
    (Array.isArray(product.imageUrls) && product.imageUrls[0]) ||
    "https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?auto=format&fit=crop&q=80&w=400";
  
  const description =
    product.description ||
    `${title} - Verified genuine automotive spare part with fitment assurance across West Africa.`;
  
  const dispatchTime = product.dispatchTime || "2-4 hrs";

  const vendorName =
    product.vendor?.name ||
    product.seller?.storeName ||
    product.seller?.name ||
    "Verified Merchant";

  const rawLocation =
    product.vendor?.area ||
    product.seller?.shopLocation ||
    product.seller?.location ||
    product.seller?.address ||
    (country === "GH" ? "Abossey Okai, Accra" : "Ladipo Market, Lagos");

  const vendorArea = rawLocation.split(",")[0].trim();
  const vendorFullAddress = product.vendor?.fullAddress || rawLocation;
  const isVerified = Boolean(product.vendor?.isVerified ?? product.seller?.isVerified ?? true);

  const handleCardClick = () => {
    if (onSelect) onSelect(product);
    else if (onProductClick) onProductClick(product);
  };

  const handleWhatsAppClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (product.whatsappUrl) {
      window.open(product.whatsappUrl, "_blank", "noopener,noreferrer");
      return;
    }
    const isGhana = currency.includes('GH') || (product as any).country_code?.toUpperCase() === 'GH';
    const fallbackPhone = isGhana ? "233539572421" : "2348000000000";
    const rawSellerPhone = product.seller?.phone?.trim();
    const phone = (rawSellerPhone && rawSellerPhone !== "2348000000000" ? rawSellerPhone : fallbackPhone).replace(/[^0-9]/g, "");
    const msg = encodeURIComponent(
      `Hello ${vendorName}, I want to order:\n\n*${title}*\nSKU: ${sku}\nPrice: ${currency} ${rawPrice.toLocaleString()}\nProduct Link: ${canonicalUrl}\n\nVehicle: ${
        activeVehicle ? `${activeVehicle.year} ${activeVehicle.make} ${activeVehicle.model}` : "Specified vehicle"
      }\n\nPlease confirm availability and payment terms.`
    );
    window.open(`https://wa.me/${phone}?text=${msg}`, "_blank", "noopener,noreferrer");
  };

  return (
    <>
      {/* Card: h-full + flex-col + justify-between for uniform grid height */}
      <div
        onClick={handleCardClick}
        className="group relative flex flex-col justify-between h-full overflow-hidden rounded-2xl border border-slate-200/60 dark:border-slate-800 bg-white dark:bg-[#0A1B4A] shadow-vite-card hover:shadow-vite-elevated hover:border-[#0A1B4A]/30 dark:hover:border-blue-400/40 transition-all duration-200 cursor-pointer"
      >
        {/* Image: aspect-square + object-cover — no stretching */}
        <div className="relative aspect-square w-full bg-slate-50 dark:bg-[#060F2A] overflow-hidden shrink-0">
          <img
            src={imageUrl}
            alt={`${title} - ${product.make || 'Genuine'} ${product.model || ''} Auto Part`}
            width={400}
            height={400}
            decoding="async"
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
            loading="lazy"
            onError={(e) => {
              (e.target as HTMLImageElement).src =
                "https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?auto=format&fit=crop&q=80&w=400";
            }}
          />
          {/* Stock badge */}
          <div className="absolute top-2 left-2 z-10">
            <span className="bg-[#0A1B4A]/85 backdrop-blur-xs text-white border-none text-[10px] px-2 py-0.5 rounded-full font-semibold shadow-xs">
              {stockCount} pcs
            </span>
          </div>
          {/* Condition badge */}
          {product.condition && (
            <div className="absolute top-2 right-2 z-10">
              <span className="bg-white/95 dark:bg-[#060F2A]/90 text-slate-800 dark:text-slate-200 text-[10px] font-bold px-2 py-0.5 rounded-full shadow-xs uppercase border border-slate-200/60 dark:border-slate-700">
                {product.condition}
              </span>
            </div>
          )}

          {/* Vehicle Make Logo Badge */}
          {product.make && (
            <div className="absolute bottom-2 left-2 z-10 flex items-center gap-1.5 bg-[#0A1B4A]/90 backdrop-blur-xs border border-white/20 px-2 py-0.5 rounded-full shadow-xs">
              <VehicleBrandLogo brand={product.make} size="xs" variant="raw" className="size-3.5 object-contain" />
              <span className="text-[10px] font-bold text-white tracking-tight">{product.make}</span>
            </div>
          )}
        </div>

        {/* Content: flex-1 fills remaining space, justify-between pushes price/CTA down */}
        <div className="flex flex-1 flex-col justify-between p-3 min-h-0">
          {/* Top section: vendor + title + metadata */}
          <div className="min-w-0">
            {/* Vendor strip with proper flex gap */}
            <div className="flex items-center gap-1 text-[10px] sm:text-[11px] font-medium text-slate-700 dark:text-slate-300 min-w-0">
              <span
                onClick={(e) => {
                  if (onVendorClick && product.seller) {
                    e.stopPropagation();
                    onVendorClick(product.seller);
                  }
                }}
                className="truncate max-w-[120px] sm:max-w-[150px] font-semibold text-slate-800 dark:text-slate-200 hover:text-blue-600 transition-colors shrink-0"
              >
                {vendorName}
              </span>
              {isVerified && (
                <ShieldCheck className="h-3 w-3 fill-blue-500 text-white shrink-0" />
              )}
              <span className="text-slate-400 dark:text-slate-500 shrink-0">•</span>
              <span className="truncate text-slate-500 dark:text-slate-400 font-medium min-w-0 flex-1">{vendorArea}</span>
            </div>

            {/* Title: line-clamp with uniform alignment */}
            <h3 className="mt-1 text-xs sm:text-[13px] font-semibold text-[#0A1B4A] dark:text-white line-clamp-2 sm:line-clamp-3 leading-[1.25em] min-h-[2.5em] sm:min-h-[3.75em] tracking-tight">
              {title}
            </h3>

            {/* Metadata row */}
            <div className="flex items-center gap-1 sm:gap-1.5 mt-1 flex-wrap">
              {product.category && (
                <span className="inline-flex items-center gap-1 text-[9px] sm:text-[10px] font-semibold text-slate-700 dark:text-slate-200 bg-slate-100 dark:bg-slate-800/80 px-1.5 sm:px-2 py-0.5 rounded-full whitespace-nowrap">
                  <CategoryIcon category={product.category} size="xs" variant="raw" className="size-3 shrink-0" />
                  <span className="truncate max-w-[75px] sm:max-w-[95px]">{product.category}</span>
                </span>
              )}
              <span className="inline-flex items-center gap-1 text-[9px] sm:text-[10px] font-bold text-[#8D840A] dark:text-[#FEF08A] bg-[#FCFBEB] dark:bg-[#8D840A]/25 border border-[#EFECC8] dark:border-[#8D840A]/40 px-1.5 sm:px-2 py-0.5 rounded-full whitespace-nowrap">
                <Zap className="h-2.5 w-2.5 fill-current" /> ACES Exact
              </span>
              <span className="inline-flex items-center gap-1 text-[9px] sm:text-[10px] font-semibold text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-950/60 border border-blue-200 dark:border-blue-800/80 px-1.5 sm:px-2 py-0.5 rounded-full whitespace-nowrap">
                <span className="size-1.5 rounded-full bg-emerald-500 animate-pulse" />
                Local Depot Stock
              </span>
              <span className="text-[9px] sm:text-[10px] font-medium text-slate-600 dark:text-slate-300 truncate">
                ⚡ {dispatchTime}
              </span>
            </div>
          </div>

          {/* Bottom section: price + specs + WhatsApp CTA */}
          <div className="mt-auto pt-2">
            {/* Price row: inline flex items-baseline — not stacked vertically */}
            <div className="flex items-baseline justify-between gap-1 border-t border-slate-100 dark:border-slate-800/80 pt-2">
              <span className="text-base sm:text-lg font-black text-[#0A1B4A] dark:text-white whitespace-nowrap tabular-nums tracking-tight">
                {currency} {rawPrice.toLocaleString()}
              </span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsSpecsOpen(true);
                }}
                className="h-6.5 text-[10px] sm:text-[11px] font-semibold border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:border-[#0A1B4A] hover:text-[#0A1B4A] dark:hover:border-blue-400 dark:hover:text-blue-300 rounded-full px-2.5 transition-colors cursor-pointer shrink-0 whitespace-nowrap"
              >
                Specs
              </button>
            </div>

            {/* Action Buttons: Add to Cart & WhatsApp */}
            <div className="mt-2 grid grid-cols-2 gap-1.5 w-full">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  addToCart(product, 1, activeVehicle);
                }}
                className="w-full bg-[#0066FF] hover:bg-[#0052CC] text-white font-bold text-[10px] sm:text-[11px] h-8 px-1 sm:px-2 rounded-xl sm:rounded-full shadow-xs transition-all flex items-center justify-center gap-1 cursor-pointer overflow-hidden"
                title="Add to Cart"
              >
                <ShoppingCart className="size-3.5 shrink-0" />
                <span className="truncate">
                  <span className="hidden sm:inline">Add to </span>Cart
                </span>
              </button>

              <button
                type="button"
                onClick={handleWhatsAppClick}
                className="w-full bg-[#0A1B4A] hover:bg-[#0f2560] dark:bg-slate-800 dark:hover:bg-slate-700 text-white font-bold text-[10px] sm:text-[11px] h-8 px-1 sm:px-2 rounded-xl sm:rounded-full shadow-xs transition-all flex items-center justify-center gap-1 cursor-pointer overflow-hidden"
                title="Order via WhatsApp"
              >
                <MessageSquare className="size-3.5 shrink-0 text-emerald-400" />
                <span className="truncate">WhatsApp</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Item Specification Modal */}
      {isSpecsOpen && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => setIsSpecsOpen(false)}
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-xs animate-in fade-in duration-150 p-0 sm:p-4"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white dark:bg-[#0A1B4A] border-0 sm:border border-slate-200/60 dark:border-slate-800 shadow-2xl rounded-none sm:rounded-3xl w-full h-full sm:h-auto sm:max-w-[440px] p-5 space-y-4 animate-in zoom-in-95 duration-150 overflow-y-auto"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 flex-wrap">
                {product.make && (
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-bold bg-[#0A1B4A]/10 dark:bg-blue-950/60 text-[#0A1B4A] dark:text-blue-300 border border-[#0A1B4A]/20 dark:border-blue-800 px-3 py-1 rounded-full">
                    <VehicleBrandLogo brand={product.make} size="xs" variant="raw" className="size-3.5 object-contain" />
                    <span>{product.make} {product.model || ''}</span>
                  </span>
                )}
                {product.category && (
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-bold bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-200 border border-slate-200 dark:border-slate-700 px-3 py-1 rounded-full">
                    <CategoryIcon category={product.category} size="xs" variant="raw" className="size-3.5 object-contain" />
                    <span>{product.category}</span>
                  </span>
                )}
                <span className="inline-flex items-center gap-1 text-[10px] font-mono font-bold tracking-tight bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 px-2.5 py-1 rounded-full">
                  <Layers className="h-3 w-3 text-[#0A1B4A] dark:text-blue-400" /> SKU: {sku}
                </span>
              </div>
              <button
                type="button"
                onClick={() => setIsSpecsOpen(false)}
                className="text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 p-1.5 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="aspect-[4/3] w-full rounded-2xl bg-slate-100 dark:bg-[#060F2A] overflow-hidden border border-slate-200/60 dark:border-slate-800">
              <img src={imageUrl} alt={`${title} - ${product.make || 'Genuine'} Auto Part Specs`} className="w-full h-full object-contain" />
            </div>

            <div>
              <h2 className="text-base font-bold text-[#0A1B4A] dark:text-white leading-snug">{title}</h2>
              <p className="text-xl font-black text-[#0A1B4A] dark:text-blue-400 mt-0.5 tabular-nums">
                {currency} {rawPrice.toLocaleString()}
              </p>
            </div>

            <div className="text-xs text-slate-600 dark:text-slate-300 space-y-3 leading-relaxed border-t border-slate-100 dark:border-slate-800 pt-3">
              <p>{description}</p>

              <div className="flex items-start gap-2 bg-slate-50 dark:bg-[#0D225C] p-3 rounded-2xl border border-slate-150 dark:border-slate-800">
                <MapPin className="h-3.5 w-3.5 text-blue-500 mt-0.5 shrink-0" />
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <p className="font-bold text-[#0A1B4A] dark:text-white text-[11px]">{vendorName}</p>
                    <span className="text-[10px] font-bold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/60 px-2 py-0.5 rounded-full border border-blue-200/60 dark:border-blue-800/60">
                      Google Local Synced
                    </span>
                  </div>
                  <p className="text-slate-500 dark:text-slate-400 mt-0.5 text-[10px]">{vendorFullAddress}</p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 pt-1">
              <button
                type="button"
                onClick={handleCopyLink}
                className="flex-1 py-2 px-3 rounded-full border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-[#0D225C] text-slate-700 dark:text-slate-200 hover:text-[#0A1B4A] dark:hover:text-white text-xs font-bold transition-all flex items-center justify-center gap-1.5 cursor-pointer"
              >
                {isCopied ? <Check className="size-3.5 text-emerald-500" /> : <Share2 className="size-3.5" />}
                <span>{isCopied ? 'Link Copied!' : 'Copy Share Link'}</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsSpecsOpen(false);
                  handleCardClick();
                }}
                className="py-2 px-4 rounded-full border border-[#0A1B4A]/30 dark:border-blue-400/40 bg-[#0A1B4A]/5 dark:bg-blue-900/30 text-[#0A1B4A] dark:text-blue-300 text-xs font-bold transition-all flex items-center justify-center gap-1 cursor-pointer"
              >
                <span>View Page</span>
                <ExternalLink className="size-3.5" />
              </button>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 pt-1">
              <button
                type="button"
                onClick={() => {
                  addToCart(product, 1, activeVehicle);
                  setIsSpecsOpen(false);
                }}
                className="flex-1 bg-[#0066FF] hover:bg-[#0052CC] text-white font-bold text-xs min-h-9 py-2.5 px-4 rounded-full shadow-xs transition-all flex items-center justify-center gap-2 cursor-pointer"
              >
                <ShoppingCart className="size-4 shrink-0" />
                <span>Add to Cart</span>
              </button>

              <button
                type="button"
                onClick={handleWhatsAppClick}
                className="flex-1 bg-[#0A1B4A] hover:bg-[#0f2560] dark:bg-blue-600 dark:hover:bg-blue-500 text-white font-bold text-xs min-h-9 py-2.5 px-4 rounded-full shadow-xs transition-all flex items-center justify-center gap-2 cursor-pointer"
              >
                <MessageSquare className="h-4 w-4 fill-white text-[#0A1B4A] dark:text-blue-600 shrink-0" />
                <span>Order via WhatsApp</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProductCard;
